/**
 * The line-icon set — Document 6 §6.1.
 *
 * Specified in Doc 6 from the beginning and built on 10 September 2026, when
 * the product owner asked for the report to carry symbols. Deliberately drawn
 * here rather than reached for:
 *
 *   No emoji. An emoji is drawn with whatever emoji font the rendering browser
 *   happens to have, so the same report would print differently from a
 *   developer's machine and from Cloudflare's renderer — and the template
 *   would stop being a pure function of the snapshot (§16.1). It would also
 *   put colour into a document whose entire palette is the studio's.
 *
 *   No icon font, no sprite sheet, no package. The report has to be
 *   self-contained: one HTML document that prints identically with no network.
 *
 * Every icon obeys the same contract, which is what makes them look like one
 * family rather than nine drawings:
 *
 *   - 24×24 viewBox, 1.5 stroke, round caps and joins
 *   - `stroke="currentColor"`, `fill="none"` — the palette tints them, and a
 *     colour literal here would survive into every studio's report
 *   - decorative: aria-hidden, and never the only carrier of meaning. Every
 *     icon in the report sits beside the label it illustrates.
 *
 * The three circumference marks are told apart by silhouette rather than by
 * anatomy: bel is an hourglass, kalça is wide at the bottom, göğüs is wide at
 * the top. See the note above PATHS for why.
 */

import type { ReactNode } from 'react';

export type IconName =
  // Physical metrics
  | 'kilo'
  | 'bmi'
  | 'bel'
  | 'kalca'
  | 'gogus'
  | 'kol'
  | 'bacak'
  | 'yag-orani'
  | 'kas-orani'
  | 'olcum'
  // Assessment dimensions
  | 'core'
  | 'denge'
  | 'esneklik'
  | 'postur'
  | 'alt-vucut'
  | 'ust-vucut'
  // Contact
  | 'telefon'
  | 'instagram'
  | 'web'
  | 'eposta'
  // Report sections
  | 'hedef'
  | 'paket'
  | 'fotograf'
  | 'degerlendirme'
  | 'takvim';

/*
 * Drawn for 17px, not for 24.
 *
 * The first set was drawn at full size and looked correct in a browser at
 * 24px; printed into a table at 17px the scale read as a padlock and the
 * torso outlines disappeared entirely, leaving three marks that differed only
 * in where a hairline sat. What survives at this size is silhouette, so each
 * mark is one recognisable shape filling most of the box, and the three
 * circumference metrics are told apart by outline — an hourglass, a shape
 * wide at the bottom, a shape wide at the top — rather than by detail.
 */
const PATHS: Record<IconName, ReactNode> = {
  /* ------------------------------------------------------------- metrics */

  /*
   * A bathroom scale. It was a hanging weight, whose trapezoid printed as a
   * near-twin of the hip mark two rows below it; a square reads as neither of
   * the trapezoids and as nothing else in the set.
   */
  kilo: (
    <>
      <path d="M3.5 4.5h17v15h-17z" />
      <path d="M8 9h8" />
      <path d="M12 13v2.5" />
    </>
  ),

  /*
   * A gauge: what a reading is. Without the needle and its pivot the arc alone
   * printed as a cloud — the closing baseline was doing the damage, so it is
   * gone and the needle carries the shape.
   */
  bmi: (
    <>
      <path d="M4 17.5a8 8 0 0 1 16 0" />
      <path d="M12 17.5l4.8-5.4" />
      <circle cx="12" cy="17.5" r="1.4" />
    </>
  ),

  // An hourglass: the waist is the narrowest point, which is the whole idea.
  bel: <path d="M6.5 3.5h11l-5.5 8.5 5.5 8.5h-11l5.5-8.5z" />,

  // Wide at the bottom.
  kalca: <path d="M9 3.5h6l3.5 17h-13z" />,

  // Wide at the top.
  gogus: <path d="M5.5 3.5h13l-3.5 17h-6z" />,

  // An arm bent at the elbow: down, then out.
  kol: <path d="M7.5 3.5v8.5a4 4 0 0 0 4 4h6.5" />,

  // A leg: down, forward at the knee, down again.
  bacak: <path d="M8.5 3.5v7l5 5v5" />,

  // A droplet — the conventional mark for a proportion of the body.
  'yag-orani': (
    <path d="M12 3S5.5 10.5 5.5 14.5a6.5 6.5 0 0 0 13 0C18.5 10.5 12 3 12 3z" />
  ),

  /* A dumbbell. Tall plates, because thin ones printed as a row of tick marks. */
  'kas-orani': (
    <>
      <path d="M6.5 12h11" />
      <path d="M6.5 6v12M17.5 6v12" />
      <path d="M3 9v6M21 9v6" />
    </>
  ),

  // A measuring tape, for a metric the studio named itself.
  olcum: (
    <>
      <path d="M2.5 8h19v8h-19z" />
      <path d="M7 8v3.5M12 8v4.5M17 8v3.5" />
    </>
  ),

  /* --------------------------------------------------------- assessment */

  // The centre held.
  core: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),

  // A point balanced on a fulcrum.
  denge: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M12 20.5l-5-8h10z" />
      <path d="M12 12.5v-9" />
    </>
  ),

  // A body reaching: one long opened-out curve.
  esneklik: (
    <>
      <path d="M4.5 20.5c0-7 4.5-11 11-12" />
      <circle cx="18.5" cy="7.5" r="2" />
    </>
  ),

  // A plumb line against a spine.
  postur: (
    <>
      <path d="M12 2.5v19" />
      <path d="M8 6.5h8M8 12h8M8 17.5h8" />
    </>
  ),

  // Legs, standing.
  'alt-vucut': (
    <>
      <path d="M7 3.5v8l-1.5 10M17 3.5v8l1.5 10" />
      <path d="M7 11.5h10" />
    </>
  ),

  // Shoulders.
  'ust-vucut': (
    <>
      <path d="M3.5 20.5V9a5.5 5.5 0 0 1 5.5-5.5h6A5.5 5.5 0 0 1 20.5 9v11.5" />
      <path d="M3.5 12.5h17" />
    </>
  ),

  /* ---------------------------------------------------------------- contact */

  // A handset.
  telefon: (
    <path d="M8.5 3.5H5A1.5 1.5 0 0 0 3.5 5c0 8.6 6.9 15.5 15.5 15.5A1.5 1.5 0 0 0 20.5 19v-3.5l-4.5-1.5-2 2.5a15 15 0 0 1-5.5-5.5l2.5-2z" />
  ),

  // The camera outline Instagram is known by. Not their glyph — a mark in the
  // same line style as the rest of the set, so the footer reads as one row.
  instagram: (
    <>
      <path d="M3.5 7.5A4 4 0 0 1 7.5 3.5h9a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9a4 4 0 0 1-4-4z" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17" cy="7" r="0.9" />
    </>
  ),

  // A globe.
  web: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5a13 13 0 0 1 0 17a13 13 0 0 1 0-17z" />
    </>
  ),

  // An envelope.
  eposta: (
    <>
      <path d="M2.5 6h19v12h-19z" />
      <path d="M2.5 6.5L12 13l9.5-6.5" />
    </>
  ),

  /* ------------------------------------------------------------ sections */

  // A target: what a goal is.
  hedef: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" />
      <circle cx="12" cy="12" r="0.6" />
    </>
  ),

  // Sessions left, as a small stack.
  paket: (
    <>
      <path d="M12 3l9 4.5-9 4.5-9-4.5z" />
      <path d="M3 12.5l9 4.5 9-4.5" />
      <path d="M3 17l9 4.5 9-4.5" />
    </>
  ),

  // A photograph.
  fotograf: (
    <>
      <path d="M2.5 7h4.5l2-3h6l2 3h4.5v13.5h-19z" />
      <circle cx="12" cy="13" r="4" />
    </>
  ),

  // A written assessment: a page with lines.
  degerlendirme: (
    <>
      <path d="M5 2.5h9.5L19 7v14.5H5z" />
      <path d="M14 2.5V7.5h5" />
      <path d="M8.5 12.5h7M8.5 16.5h4.5" />
    </>
  ),

  // A calendar, for a reporting period.
  takvim: (
    <>
      <path d="M3.5 6h17v15.5h-17z" />
      <path d="M3.5 10.5h17" />
      <path d="M8 3.5v5M16 3.5v5" />
    </>
  ),
};

export interface IconProps {
  name: IconName;
  /** Rendered size in px. The stroke stays 1.5 at 24 and scales with it. */
  size?: number;
  className?: string;
}

/**
 * Decorative by construction: `aria-hidden` and no title.
 *
 * Every icon in the report sits next to the words it illustrates, so a reader
 * who cannot see it loses nothing. An icon that carried meaning on its own
 * would need a label, and would be the wrong design for a document a studio
 * hands to a student.
 */
export function Icon({ name, size = 18, className }: IconProps) {
  return (
    <svg
      className={className ? `pp-icon ${className}` : 'pp-icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

/** The icon for a metric row, or the generic tape for a studio's own metric. */
export function metricIcon(key: string): IconName {
  const byKey: Record<string, IconName> = {
    weight: 'kilo',
    bmi: 'bmi',
    waist: 'bel',
    hip: 'kalca',
    chest: 'gogus',
    rightArm: 'kol',
    leftArm: 'kol',
    rightThigh: 'bacak',
    leftThigh: 'bacak',
    bodyFat: 'yag-orani',
    muscle: 'kas-orani',
  };

  return byKey[key] ?? 'olcum';
}

/** The icon for an assessment dimension. */
export function assessmentIcon(key: string): IconName {
  const byKey: Record<string, IconName> = {
    coreControl: 'core',
    balance: 'denge',
    flexibility: 'esneklik',
    lowerBodyStrength: 'alt-vucut',
    upperBodyStrength: 'ust-vucut',
  };

  return byKey[key] ?? 'degerlendirme';
}

/**
 * The icon for a goal.
 *
 * A goal on a measured metric borrows that metric's mark, so the goal card and
 * the physical table read as being about the same thing. A goal with no
 * measurement behind it gets the target.
 */
export function goalIcon(goalType: string): IconName {
  const byType: Record<string, IconName> = {
    weight: 'kilo',
    waist: 'bel',
    hip: 'kalca',
    body_fat: 'yag-orani',
    muscle: 'kas-orani',
    flexibility: 'esneklik',
    balance: 'denge',
    core_control: 'core',
    posture: 'postur',
  };

  return byType[goalType] ?? 'hedef';
}
